import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useCreateRun } from '../../hooks/useRuns'
import { useAuth } from '../../hooks/useAuth'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog'
import { Button } from '../ui/button'
import { ValidatedInput, ValidatedTextarea, ValidationSummary, useFieldValidation } from '../forms/ValidatedInput'
import { createRunSchema, useFormValidation } from '../../lib/validations'

interface CreateRunDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function CreateRunDialog({ open, onOpenChange }: CreateRunDialogProps) {
  const navigate = useNavigate()
  const { user } = useAuth()
  const createRunMutation = useCreateRun()
  const { validate } = useFormValidation(createRunSchema)
  const { getFieldError } = useFieldValidation()
  
  const [formData, setFormData] = useState({
    title: '',
    date: '',
    notes: ''
  })
  const [validationErrors, setValidationErrors] = useState<Record<string, string[]>>()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!user) return

    // Validate form data
    const validation = validate(formData)
    if (!validation.success) {
      setValidationErrors(validation.errors)
      return
    }

    // Clear validation errors on successful validation
    setValidationErrors(undefined)

    try {
      const newRun = await createRunMutation.mutateAsync({
        title: validation.data!.title,
        date: validation.data!.date || null,
        notes: validation.data!.notes || null,
        user_id: user.id
      })
      
      onOpenChange(false)
      setFormData({ title: '', date: '', notes: '' })
      setValidationErrors(undefined)
      navigate(`/runs/${newRun.id}`)
    } catch (error) {
      console.error('Failed to create run:', error)
      setValidationErrors({ general: ['Failed to create run. Please try again.'] })
    }
  }

  const handleFieldChange = (field: keyof typeof formData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    // Clear field-specific validation error when user starts typing
    if (validationErrors?.[field]) {
      const newErrors = { ...validationErrors }
      delete newErrors[field]
      setValidationErrors(Object.keys(newErrors).length > 0 ? newErrors : undefined)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create New Run</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <ValidationSummary errors={validationErrors || {}} />
          
          <ValidatedInput
            id="title"
            label="Title"
            value={formData.title}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFieldChange('title', e.target.value)}
            placeholder="e.g., Boston Marathon 2024"
            required
            error={getFieldError(validationErrors, 'title')}
          />

          <ValidatedInput
            id="date"
            label="Date"
            type="date"
            value={formData.date}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFieldChange('date', e.target.value)}
            error={getFieldError(validationErrors, 'date')}
          />

          <ValidatedTextarea
            id="notes"
            label="Notes"
            value={formData.notes}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleFieldChange('notes', e.target.value)}
            placeholder="Race notes, goals, logistics..."
            rows={3}
            error={getFieldError(validationErrors, 'notes')}
          />

          <div className="flex justify-end space-x-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={createRunMutation.isPending}>
              {createRunMutation.isPending ? 'Creating...' : 'Create Run'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}